import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getSettings, saveSettings, defaultPremium, defaultThemeColors } from "../utils/storage";
import { services as mockServices } from "../mock";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Toaster, toast } from "sonner";

const sectionStyle = "bg-white/5 backdrop-blur-2xl border-white/10";

export default function Config() {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load settings from backend on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await getSettings();
        // Ensure services are seeded at least once
        if (!data.services || data.services.length === 0) {
          const seeded = mockServices.map((s) => ({ ...s, images: [] }));
          const next = { ...data, services: seeded };
          setSettings(next);
          // Don't save here, let user save manually
        } else {
          setSettings(data);
        }
      } catch (error) {
        console.error("Failed to load settings:", error);
        toast.error("Erro ao carregar configurações");
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const update = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      await saveSettings(next);
      toast.success("Configurações salvas no servidor");
    } catch (error) {
      toast.error("Erro ao salvar configurações");
      console.error("Save error:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen text-white flex items-center justify-center" style={{ background: "#0b1b34" }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <p>Carregando configurações...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen text-white" style={{ background: "#0b1b34" }}>
        <Toaster richColors position="top-right" />
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-24">
          <h1 className="text-4xl font-extrabold">Configurações</h1>
          <p className="mt-2 text-white/70 max-w-2xl">Área de personalização da HÉRCULES. Faça login para editar contatos, estatísticas, serviços e cores.</p>
          <div className="h-[50vh]" />
          <div className="flex justify-center">
            <Button asChild className="bg-yellow-400 text-black hover:bg-yellow-300">
              <Link to="/login">Admin</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Admin UI
  return (
    <div className="min-h-screen text-white" style={{ background: "#0b1b34" }}>
      <Toaster richColors position="top-right" />
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 space-y-10">
        <header>
          <h1 className="text-4xl font-extrabold">Administração</h1>
          <p className="text-white/70">Edite os conteúdos do site</p>
        </header>

        {/* Contatos */}
        <Card className={sectionStyle}>
          <CardHeader>
            <CardTitle className="text-white">Contatos</CardTitle>
            <CardDescription className="text-white/70">WhatsApp e Instagram dos botões</CardDescription>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white">WhatsApp (link completo)</Label>
              <Input className="bg-white/10 border-white/20 text-white" value={settings.contact.whatsapp}
                onChange={(e) => setSettings({ ...settings, contact: { ...settings.contact, whatsapp: e.target.value } })} />
            </div>
            <div>
              <Label className="text-white">Instagram (link)</Label>
              <Input className="bg-white/10 border-white/20 text-white" value={settings.contact.instagram}
                onChange={(e) => setSettings({ ...settings, contact: { ...settings.contact, instagram: e.target.value } })} />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <Button className="bg-yellow-400 text-black hover:bg-yellow-300" onClick={() => update({ contact: settings.contact })}>Salvar</Button>
            </div>
          </CardContent>
        </Card>

        {/* Estatísticas */}
        <Card className={sectionStyle}>
          <CardHeader>
            <CardTitle className="text-white">Estatísticas</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-white">Projetos entregues</Label>
              <Input className="bg-white/10 border-white/20 text-white" value={settings.stats.entregues}
                onChange={(e) => setSettings({ ...settings, stats: { ...settings.stats, entregues: e.target.value } })} />
            </div>
            <div>
              <Label className="text-white">Anos de mercado</Label>
              <Input className="bg-white/10 border-white/20 text-white" value={settings.stats.anos}
                onChange={(e) => setSettings({ ...settings, stats: { ...settings.stats, anos: e.target.value } })} />
            </div>
            <div>
              <Label className="text-white">Clientes ativos</Label>
              <Input className="bg-white/10 border-white/20 text-white" value={settings.stats.clientes}
                onChange={(e) => setSettings({ ...settings, stats: { ...settings.stats, clientes: e.target.value } })} />
            </div>
            <div className="md:col-span-3 flex justify-end">
              <Button className="bg-yellow-400 text-black hover:bg-yellow-300" onClick={() => update({ stats: settings.stats })}>Salvar</Button>
            </div>
          </CardContent>
        </Card>

        {/* Produção Premium */}
        <Card className={sectionStyle}>
          <CardHeader>
            <CardTitle className="text-white">Produção Premium (Prazos)</CardTitle>
            <CardDescription className="text-white/70">Defina o rótulo e a barra de prazo para cada opção</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(settings.premium || defaultPremium).map(([k, cfg], idx) => (
              <div key={idx} className="grid md:grid-cols-3 gap-3 items-center">
                <div className="font-semibold">{k}</div>
                <Input className="bg-white/10 border-white/20 text-white" value={cfg.label}
                  onChange={(e) => {
                    const next = { ...(settings.premium || {}), [k]: { ...cfg, label: e.target.value } };
                    setSettings({ ...settings, premium: next });
                  }} />
                <div className="flex items-center gap-3">
                  <input type="range" min={10} max={100} value={cfg.value}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      const next = { ...(settings.premium || {}), [k]: { ...cfg, value: v } };
                      setSettings({ ...settings, premium: next });
                    }}
                  />
                  <span className="w-16 text-right text-white/80">{cfg.value}%</span>
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <Button className="bg-yellow-400 text-black hover:bg-yellow-300" onClick={() => update({ premium: settings.premium })}>Salvar</Button>
            </div>
          </CardContent>
        </Card>

        {/* Cores de fundo por seção */}
        <Card className={sectionStyle}>
          <CardHeader>
            <CardTitle className="text-white">Cores por Seção</CardTitle>
            <CardDescription className="text-white/70">Altera o fundo ao rolar</CardDescription>
          </CardHeader>
          <CardContent className="grid md:grid-cols-5 gap-4">
            {Object.entries(settings.themeColors || defaultThemeColors).map(([key, val]) => (
              <div key={key}>
                <Label className="text-white capitalize">{key}</Label>
                <Input type="color" value={val} onChange={(e) => {
                  const next = { ...(settings.themeColors || {}), [key]: e.target.value };
                  setSettings({ ...settings, themeColors: next });
                }} />
              </div>
            ))}
            <div className="md:col-span-5 flex justify-end">
              <Button className="bg-yellow-400 text-black hover:bg-yellow-300" onClick={() => update({ themeColors: settings.themeColors })}>Salvar</Button>
            </div>
          </CardContent>
        </Card>

        {/* Serviços */}
        <Card className={sectionStyle}>
          <CardHeader>
            <CardTitle className="text-white">Serviços</CardTitle>
            <CardDescription className="text-white/70">Adicione, edite, remova serviços e imagens</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Dialog>
              <DialogTrigger asChild>
                <Button className="bg-yellow-400 text-black hover:bg-yellow-300">Adicionar serviço</Button>
              </DialogTrigger>
              <ServiceEditor onSave={(srv) => {
                const next = { ...settings, services: [...(settings.services||[]), srv] };
                setSettings(next); saveSettings(next); toast.success("Serviço adicionado");
              }} />
            </Dialog>

            <Separator className="my-4 bg-white/10" />

            <div className="grid gap-4">
              {settings.services?.map((s, idx) => (
                <Card key={idx} className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">{s.title}</CardTitle>
                    <CardDescription className="text-white/70">{s.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {s.highlights?.map((t, i) => (
                        <Badge key={i} className="bg-white/10 border-white/20 text-white">{t}</Badge>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {s.images?.map((url, i) => (
                        <img key={i} src={url} alt="img" className="w-20 h-14 object-cover rounded" />
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">Editar</Button>
                        </DialogTrigger>
                        <ServiceEditor
                          initial={s}
                          onSave={(srv) => {
                            const list = [...settings.services];
                            list[idx] = srv;
                            const next = { ...settings, services: list };
                            setSettings(next); saveSettings(next); toast.success("Serviço atualizado");
                          }}
                        />
                      </Dialog>
                      <Button variant="destructive" onClick={() => {
                        const list = settings.services.filter((_, i) => i !== idx);
                        const next = { ...settings, services: list };
                        setSettings(next); saveSettings(next); toast.success("Serviço removido");
                      }}>Excluir</Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ServiceEditor({ initial, onSave }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [highlights, setHighlights] = useState(initial?.highlights?.join(", ") || "");
  const [images, setImages] = useState(initial?.images || []);

  const addImageUrl = (e) => {
    e.preventDefault();
    const url = e.target.form.elements["imgUrl"].value;
    if (!url) return;
    setImages((prev) => [...prev, url]);
    e.target.form.reset();
  };

  const addImageFile = (file) => {
    const reader = new FileReader();
    reader.onload = () => setImages((prev) => [...prev, reader.result]);
    reader.readAsDataURL(file);
  };

  return (
    <DialogContent className="bg-[#0b1b34] text-white border-white/10 max-w-2xl">
      <DialogHeader>
        <DialogTitle className="text-white">{initial ? "Editar serviço" : "Novo serviço"}</DialogTitle>
      </DialogHeader>
      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label className="text-white">Título</Label>
          <Input className="bg-white/10 border-white/20 text-white" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="grid gap-2">
          <Label className="text-white">Descrição</Label>
          <Textarea className="bg-white/10 border-white/20 text-white" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
        </div>
        <div className="grid gap-2">
          <Label className="text-white">Tags (separadas por vírgula)</Label>
          <Input className="bg-white/10 border-white/20 text-white" value={highlights} onChange={(e) => setHighlights(e.target.value)} />
        </div>
        <div className="grid gap-2">
          <Label className="text-white">Adicionar imagem (URL)</Label>
          <div className="flex gap-2">
            <Input name="imgUrl" className="bg-white/10 border-white/20 text-white" placeholder="https://..." />
            <Button onClick={addImageUrl} className="bg-yellow-400 text-black hover:bg-yellow-300">Adicionar</Button>
          </div>
          <Label className="text-white mt-2">Ou enviar arquivo</Label>
          <Input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && addImageFile(e.target.files[0])} />
          <div className="flex flex-wrap gap-2 mt-2">
            {images.map((url, i) => (
              <div key={i} className="relative">
                <img src={url} alt="img" className="w-24 h-16 object-cover rounded" />
                <button className="absolute -top-2 -right-2 bg-black/70 text-white text-xs rounded px-1" onClick={() => setImages(images.filter((_, idx) => idx !== i))}>x</button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <DialogTrigger asChild>
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">Cancelar</Button>
          </DialogTrigger>
          <DialogTrigger asChild>
            <Button className="bg-yellow-400 text-black hover:bg-yellow-300" onClick={() => {
              const payload = {
                id: (initial?.id || title.toLowerCase().replace(/\s+/g, "-")),
                title,
                description,
                highlights: highlights.split(",").map((t) => t.trim()).filter(Boolean),
                images,
              };
              onSave(payload);
            }}>Salvar</Button>
          </DialogTrigger>
        </div>
      </div>
    </DialogContent>
  );
}
